/** ROS service to load an STCM map on the robot (slamware SDK). */
export const SYNC_SET_STCM_SERVICE = "/slamware_ros_sdk_server_node/sync_set_stcm";
export const SYNC_SET_STCM_TYPE = "slamware_ros_sdk/srv/SyncSetStcm";

/** Mission LoadMap pipeline (r2c2_actions) — same as mission executor. */
export const LOAD_MAP_ACTION_NAME = "/load_map";
export const LOAD_MAP_ACTION_TYPE = "r2c2_actions_interfaces/action/LoadMap";

/** Aurora upload used internally by LoadMap; also the STCM transfer step. */
export const AURORA_SET_STCM_ACTION = "/aurora/set_stcm";
export const AURORA_SET_STCM_TYPE = "slamware_ros_sdk/action/SetStcm";

/**
 * mission_storage base URL as seen from the robot (Aurora fetches map bytes here).
 * Defaults to localhost; override when mission_storage is not co-located with the ARC.
 */
export function getMissionStorageUrlOnRobot(): string {
    const raw = process.env.REACT_APP_MISSION_STORAGE_URL_ON_ROBOT?.trim();
    return (raw || "http://localhost:8000").replace(/\/$/, "");
}

/** HTTP URI for `/aurora/set_stcm` (mission LoadMap STCM fetch target). */
export function missionStorageStcmUri(mapId: string): string {
    const id = mapId.trim();
    if (!id) {
        throw new Error("map_id is required");
    }
    return `${getMissionStorageUrlOnRobot()}/map_files/${id}/stcm/stcm`;
}

/**
 * Awaitable clear map: device requireMapReset + reinit_aurora on the Aurora server.
 * Callers should not also call reinit_aurora after this succeeds.
 */
export const PERFORM_CLEAR_MAP_SERVICE = "/slamware_ros_sdk_server_node/perform_clear_map";
export const PERFORM_CLEAR_MAP_TYPE = "std_srvs/srv/Trigger";

/** Reconnect Aurora SDK and full explore-map sync (like aurora.service restart). */
export const REINIT_AURORA_SERVICE = "/slamware_ros_sdk_server_node/reinit_aurora";
export const REINIT_AURORA_TYPE = "std_srvs/srv/Trigger";
/** Server explore-map sync alone can take up to ~90s; leave headroom for reconnect. */
export const REINIT_AURORA_TIMEOUT_MS = 120_000;

/** Clear keep-out filter via load_map_action_server (neutral / placeholder mask). */
export const CLEAR_KEEPOUT_FILTER_SERVICE = "/r2c2/clear_keepout_filter";
export const CLEAR_KEEPOUT_FILTER_TYPE = "r2c2_actions_interfaces/srv/ClearKeepoutFilter";

export type ClearKeepoutFilterMode = "stcm" | "grid" | "placeholder" | "auto";

export type ClearKeepoutFilterRequest = {
    mode: ClearKeepoutFilterMode;
    stcm_rel_path?: string;
    wait_for_metadata?: boolean;
    baseline_geometry_key?: string;
    baseline_map_load_time_ns?: number;
    baseline_metadata_seq?: number;
    width?: number;
    height?: number;
    resolution?: number;
    origin_x?: number;
    origin_y?: number;
    origin_yaw?: number;
};

export function clearKeepoutFilterService(request: ClearKeepoutFilterRequest) {
    return [
        CLEAR_KEEPOUT_FILTER_SERVICE,
        {
            mode: request.mode,
            stcm_rel_path: request.stcm_rel_path ?? "",
            wait_for_metadata: request.wait_for_metadata ?? false,
            baseline_geometry_key: request.baseline_geometry_key ?? "",
            baseline_map_load_time_ns: request.baseline_map_load_time_ns ?? 0,
            baseline_metadata_seq: request.baseline_metadata_seq ?? 0,
            width: request.width ?? 0,
            height: request.height ?? 0,
            resolution: request.resolution ?? 0,
            origin_x: request.origin_x ?? 0,
            origin_y: request.origin_y ?? 0,
            origin_yaw: request.origin_yaw ?? 0,
        },
        {
            serviceType: CLEAR_KEEPOUT_FILTER_TYPE,
            timeoutMs: request.wait_for_metadata ? REINIT_AURORA_TIMEOUT_MS : 30_000,
        },
    ] as const;
}

/**
 * perform_clear_map awaits device reset + full Aurora reinit (~90s explore sync).
 * Leave headroom above REINIT_AURORA_TIMEOUT_MS for requireMapReset.
 */
export const PERFORM_CLEAR_MAP_TIMEOUT_MS = 220_000;

export function performClearMap() {
    return [
        PERFORM_CLEAR_MAP_SERVICE,
        {},
        {
            serviceType: PERFORM_CLEAR_MAP_TYPE,
            timeoutMs: PERFORM_CLEAR_MAP_TIMEOUT_MS,
        },
    ] as const;
}

export function reinitAurora() {
    return [
        REINIT_AURORA_SERVICE,
        {},
        { serviceType: REINIT_AURORA_TYPE, timeoutMs: REINIT_AURORA_TIMEOUT_MS },
    ] as const;
}

export function getMapStcmRoot(): string {
    return (process.env.REACT_APP_MAP_STCM_ROOT?.trim() || "/home/r2c2/map").replace(
        /\/$/,
        ""
    );
}

/** Build absolute mapfile path on the robot from a relative path returned by the map list API. */
export function relativeStcmToAbsolute(relativePath: string): string {
    const norm = relativePath.replace(/\\/g, "/").replace(/^\/+/, "");
    if (!norm || norm.split("/").includes("..")) {
        throw new Error("Invalid map path");
    }
    if (!norm.toLowerCase().endsWith(".stcm")) {
        throw new Error("Map file must be .stcm");
    }
    return `${getMapStcmRoot()}/${norm}`;
}

/** Any storage-relative path under the map root (e.g. `.bmp`/`.png`, `_filter.yaml`). */
export function relativeMapStorageToAbsolute(relativePath: string): string {
    const norm = relativePath.replace(/\\/g, "/").replace(/^\/+/, "");
    if (!norm || norm.split("/").includes("..")) {
        throw new Error("Invalid map path");
    }
    return `${getMapStcmRoot()}/${norm}`;
}

/** `{dir/}name.stcm` → `{dir/}name_filter.yaml` */
export function stcmRelativePathToFilterYamlRelative(stcmRelativePath: string): string {
    const n = stcmRelativePath.replace(/\\/g, "/").trim();
    if (!n.toLowerCase().endsWith(".stcm")) {
        return `${n}_filter.yaml`;
    }
    return n.replace(/\.stcm$/i, "_filter.yaml");
}

/** Arguments for `callService` / arcjs ServiceCaller (optional third: service type). */
export function syncSetStcm(absoluteMapfile: string) {
    return [
        SYNC_SET_STCM_SERVICE,
        { mapfile: absoluteMapfile },
        { serviceType: SYNC_SET_STCM_TYPE },
    ] as const;
}

/** ROS service to save current map to .stcm + 2D image (slamware SDK). */
export const SYNC_GET_STCM_SERVICE = "/slamware_ros_sdk_server_node/sync_get_stcm";
export const SYNC_GET_STCM_TYPE = "slamware_ros_sdk/srv/SyncGetStcm";

export function syncGetStcm(mapfile: string, path_2d: string) {
    return [
        SYNC_GET_STCM_SERVICE,
        { mapfile, path_2d },
        { serviceType: SYNC_GET_STCM_TYPE },
    ] as const;
}

const MAP_BASE_NAME_RE = /^[a-zA-Z0-9]([a-zA-Z0-9_.-]{0,199})$/;

/** Validate user-entered map base name (no extension). Throws if invalid. */
export function validateAndNormalizeMapBaseName(raw: string): string {
    const s = raw.trim();
    if (!s) {
        throw new Error("Name is required");
    }
    if (s.length > 200) {
        throw new Error("Name is too long");
    }
    if (!MAP_BASE_NAME_RE.test(s)) {
        throw new Error(
            "No spaces. Use only letters, numbers, dot, underscore, or hyphen; must start with a letter or number"
        );
    }
    if (s === "." || s === ".." || s.endsWith(".")) {
        throw new Error("Invalid name");
    }
    return s;
}

/** Publish to clear map (slamware SDK). Tuple for `sendCommand(...tuple)`. */
export const CLEAR_MAP_TOPIC = "/slamware_ros_sdk_server_node/clear_map";
export const CLEAR_MAP_MSG_TYPE = "slamware_ros_sdk/msg/ClearMapRequest";

export function clearMapPublish() {
    return [CLEAR_MAP_TOPIC, {}, CLEAR_MAP_MSG_TYPE] as const;
}

/** Enable/disable live mapping updates (slamware SDK). */
export const SET_MAP_UPDATE_TOPIC = "/slamware_ros_sdk_server_node/set_map_update";
export const SET_MAP_UPDATE_MSG_TYPE = "slamware_ros_sdk/msg/SetMapUpdateRequest";

export function setMapUpdatePublish(enabled: boolean) {
    return [SET_MAP_UPDATE_TOPIC, { enabled: Boolean(enabled) }, SET_MAP_UPDATE_MSG_TYPE] as const;
}

/** Lowercased stem (no .stcm) for each listed relative path — for uniqueness checks. */
export function existingStcmBaseNamesLower(files: unknown): Set<string> {
    const out = new Set<string>();
    if (!Array.isArray(files)) return out;
    for (const value of files) {
        if (typeof value !== "string" || !value.trim()) continue;
        const normalized = value.replace(/\\/g, "/").trim();
        const base = normalized.split("/").pop() ?? normalized;
        const stem = base.replace(/\.stcm$/i, "").toLowerCase();
        if (stem) {
            out.add(stem);
        }
    }
    return out;
}

/** Stems from stored .stcm maps plus standalone edited image paths — for “save map” / name collision checks. */
export function existingStorageMapStemSet(stcmFiles: unknown, editedBmpRelPaths: unknown): Set<string> {
    const out = existingStcmBaseNamesLower(stcmFiles);
    if (!Array.isArray(editedBmpRelPaths)) return out;
    for (const value of editedBmpRelPaths) {
        if (typeof value !== "string" || !value.trim()) continue;
        const normalized = value.replace(/\\/g, "/").trim();
        const base = normalized.split("/").pop() ?? normalized;
        const stem = base.replace(/\.(bmp|png)$/i, "").toLowerCase();
        if (stem) {
            out.add(stem);
        }
    }
    return out;
}
